// Phase 1A: Contract Knowledge Base — Admin-only Contract Library
// Basic admin interface. No polish. Isolated from existing pages.
import React from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Uploaded": "bg-slate-100 text-slate-700",
  "Extracting": "bg-blue-100 text-blue-800",
  "Sample Ready for Review": "bg-amber-100 text-amber-800",
  "Sample Approved": "bg-green-100 text-green-800",
  "Processing Full Agreement": "bg-blue-100 text-blue-800",
  "Full Agreement Ready for Review": "bg-purple-100 text-purple-800",
  "Active": "bg-emerald-100 text-emerald-800",
  "Failed": "bg-red-100 text-red-800",
};

export function AdminContractLibrary() {
  const [rows, setRows] = React.useState([]);
  const [file, setFile] = React.useState(null);
  const [form, setForm] = React.useState({
    title: "UPS National Master Agreement",
    agreement_type: "National Master Agreement",
    effective_start_date: "2023-08-01",
    effective_end_date: "2028-07-31",
    version: "2023-2028",
  });
  const [busy, setBusy] = React.useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/contracts");
    setRows(data);
  };
  React.useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF first");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      await api.post("/admin/contracts/upload", fd, { headers: {"Content-Type":"multipart/form-data"} });
      toast.success("Uploaded. Next: click 'Extract Article 1 sample'.");
      setFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const extractSample = async (id) => {
    try { await api.post(`/admin/contracts/${id}/extract-sample`); toast.success("Sample extracted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Extraction failed"); load(); }
  };
  const approveAndProcess = async (id) => {
    if (!window.confirm("Approve Article 1 sample and process the full agreement? This may take a minute.")) return;
    try { await api.post(`/admin/contracts/${id}/approve-sample-and-process`); toast.success("Full agreement processed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); load(); }
  };
  const reprocess = async (id) => {
    if (!window.confirm("Reprocess the full agreement? Existing extracted records will be replaced.")) return;
    try { await api.post(`/admin/contracts/${id}/reprocess`); toast.success("Reprocessed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); load(); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/contracts/${id}`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this agreement and ALL its extracted records? This cannot be undone.")) return;
    try { await api.delete(`/admin/contracts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black font-display" data-testid="kb-title">Contract Library</h1>
        <div className="flex gap-2">
          <Link to="/admin/contracts/viewer"><Button variant="outline" data-testid="kb-viewer-link">Contract Viewer</Button></Link>
          <Link to="/admin/contracts/search"><Button variant="outline" data-testid="kb-search-link">Search</Button></Link>
          <Link to="/admin"><Button variant="ghost">Back to Admin</Button></Link>
        </div>
      </div>
      <p className="text-sm text-slate-500 mt-1">Phase 1A — Admin-only. Isolated from existing contract data.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="kb-upload-form">
        <h2 className="font-bold font-display text-lg">Upload a new agreement</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">Document Title
            <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="kb-title-input"/>
          </label>
          <label className="text-sm">Agreement Type
            <select className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={form.agreement_type} onChange={e=>setForm({...form,agreement_type:e.target.value})} data-testid="kb-type-select">
              <option>National Master Agreement</option>
              <option>Regional Supplement</option>
              <option>Local Rider</option>
              <option>Memorandum of Understanding</option>
              <option>Other</option>
            </select>
          </label>
          <label className="text-sm">Effective start
            <input type="date" className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={form.effective_start_date} onChange={e=>setForm({...form,effective_start_date:e.target.value})}/>
          </label>
          <label className="text-sm">Effective end
            <input type="date" className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={form.effective_end_date} onChange={e=>setForm({...form,effective_end_date:e.target.value})}/>
          </label>
          <label className="text-sm">Version
            <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={form.version} onChange={e=>setForm({...form,version:e.target.value})}/>
          </label>
          <label className="text-sm">PDF file
            <input type="file" accept="application/pdf" onChange={e=>setFile(e.target.files?.[0]||null)} className="mt-1 block" data-testid="kb-file-input"/>
          </label>
        </div>
        <Button disabled={busy} onClick={upload} className="mt-4 bg-red-600 hover:bg-red-700 text-white" data-testid="kb-upload-btn">
          {busy ? "Uploading..." : "Upload agreement"}
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Version</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Processing</th>
              <th className="text-left p-3">Uploaded</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="kb-agreements-table">
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No agreements uploaded yet.</td></tr>}
            {rows.map(r=>(
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`kb-row-${r.id}`}>
                <td className="p-3 font-medium">{r.title}<div className="text-xs text-slate-500">{r.agreement_type}</div></td>
                <td className="p-3">{r.version}</td>
                <td className="p-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{r.status}</span></td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[r.processing_status] || "bg-slate-100"}`}>{r.processing_status}</span>
                  {r.last_error && <div className="text-xs text-red-600 mt-1">{r.last_error}</div>}
                </td>
                <td className="p-3 text-xs text-slate-500">{r.uploaded_at?.slice(0,10)}</td>
                <td className="p-3 space-x-1 space-y-1">
                  {(r.processing_status === "Uploaded" || r.processing_status === "Failed") && (
                    <Button size="sm" onClick={()=>extractSample(r.id)} data-testid={`kb-extract-${r.id}`}>Extract Article 1 sample</Button>
                  )}
                  {r.processing_status === "Sample Ready for Review" && (
                    <>
                      <Link to={`/admin/contracts/${r.id}/review?sample=1`}><Button size="sm" variant="outline">Review Article 1</Button></Link>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={()=>approveAndProcess(r.id)} data-testid={`kb-approve-${r.id}`}>Approve Sample and Process Full Agreement</Button>
                    </>
                  )}
                  {r.processing_status === "Full Agreement Ready for Review" && (
                    <>
                      <Link to={`/admin/contracts/${r.id}/review`}><Button size="sm" variant="outline">Review structure</Button></Link>
                      {r.status !== "Active"
                        ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={()=>setStatus(r.id, "Active")}>Activate</Button>
                        : <Button size="sm" variant="outline" onClick={()=>setStatus(r.id, "Inactive")}>Deactivate</Button>}
                      <Button size="sm" variant="outline" onClick={()=>reprocess(r.id)}>Reprocess</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={()=>remove(r.id)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

// =============================================================================
// Review & correction interface
// =============================================================================
export function AdminContractReview() {
  const params = new URLSearchParams(window.location.search);
  const sampleOnly = params.get("sample") === "1";
  const aid = window.location.pathname.split("/")[3];
  const [struct, setStruct] = React.useState({articles:[], sections:[], provisions:[]});
  const [topics, setTopics] = React.useState([]);
  const [keywords, setKeywords] = React.useState([]);
  const [expanded, setExpanded] = React.useState({});
  const [editingProv, setEditingProv] = React.useState(null);
  const [editingSection, setEditingSection] = React.useState(null);
  const [editingArticle, setEditingArticle] = React.useState(null);

  const load = async () => {
    const [s,t,k] = await Promise.all([
      api.get(`/admin/contracts/${aid}/structure${sampleOnly ? "?sample_only=true" : ""}`),
      api.get(`/admin/contracts/meta/topics`),
      api.get(`/admin/contracts/meta/keywords`),
    ]);
    setStruct(s.data); setTopics(t.data); setKeywords(k.data);
  };
  React.useEffect(() => { load(); }, []);

  const saveArticle = async () => {
    await api.patch(`/admin/contracts/articles/${editingArticle.id}`, editingArticle);
    toast.success("Article updated"); setEditingArticle(null); load();
  };
  const saveSection = async () => {
    await api.patch(`/admin/contracts/sections/${editingSection.id}`, editingSection);
    toast.success("Section updated"); setEditingSection(null); load();
  };
  const saveProvision = async () => {
    await api.patch(`/admin/contracts/provisions/${editingProv.id}`, editingProv);
    toast.success("Provision updated"); setEditingProv(null); load();
  };
  const toggleTopic = async (prov, topicId) => {
    const cur = new Set(prov.topic_ids || []);
    cur.has(topicId) ? cur.delete(topicId) : cur.add(topicId);
    await api.put(`/admin/contracts/provisions/${prov.id}/topics`, { topic_ids: [...cur] });
    load();
  };
  const toggleKeyword = async (prov, kwId) => {
    const cur = new Set(prov.keyword_ids || []);
    cur.has(kwId) ? cur.delete(kwId) : cur.add(kwId);
    await api.put(`/admin/contracts/provisions/${prov.id}/keywords`, { keyword_ids: [...cur] });
    load();
  };
  const setReview = async (prov, review_status) => {
    await api.patch(`/admin/contracts/provisions/${prov.id}`, { review_status });
    load();
  };
  const doMerge = async () => {
    const ids = Object.entries(expanded).filter(([_,v])=>v==="merge").map(([id])=>id);
    if (ids.length < 2) return toast.info("Mark at least 2 provisions with 'Select for merge' first.");
    if (!window.confirm(`Merge ${ids.length} provisions into the first one?`)) return;
    await api.post(`/admin/contracts/provisions/merge`, { provision_ids: ids });
    toast.success("Merged"); setExpanded({}); load();
  };
  const doSplit = async (prov) => {
    const at = prompt(`Split at what character offset? (Text length: ${prov.exact_source_text.length})`);
    const offset = parseInt(at, 10);
    if (!offset) return;
    await api.post(`/admin/contracts/provisions/${prov.id}/split`, { split_at: offset });
    toast.success("Split"); load();
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black font-display" data-testid="kb-review-title">Review Structure {sampleOnly && <span className="text-sm text-amber-700">(Article 1 sample only)</span>}</h1>
          <p className="text-xs text-slate-500 mt-1">Rename, correct, split, merge, and assign topics/keywords. When done, use back button to approve.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doMerge} data-testid="kb-merge-btn">Merge selected</Button>
          <Link to="/admin/contracts"><Button variant="ghost">← Contract Library</Button></Link>
        </div>
      </div>

      {struct.articles.map(art=>{
        const artSections = struct.sections.filter(s=>s.article_id===art.id);
        const artProvsNoSection = struct.provisions.filter(p=>p.article_id===art.id && !p.section_id);
        return (
          <div key={art.id} className="mt-6 bg-white border border-slate-200 rounded-md" data-testid={`kb-article-${art.id}`}>
            <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200">
              <div>
                <div className="text-xs font-semibold text-red-700 tracking-widest uppercase">Article {art.article_number}</div>
                <div className="font-bold font-display">{art.article_title || <span className="text-slate-400 italic">(no title)</span>}</div>
                <div className="text-xs text-slate-500 mt-1">Pages {art.starting_page}–{art.ending_page} · Status: {art.review_status}</div>
              </div>
              <Button size="sm" variant="outline" onClick={()=>setEditingArticle(art)}>Edit article</Button>
            </div>
            {artSections.length === 0 && artProvsNoSection.length > 0 && (
              <div className="p-4">
                {artProvsNoSection.map(p => <ProvisionRow key={p.id} prov={p} topics={topics} keywords={keywords}
                  expanded={expanded} setExpanded={setExpanded}
                  onEdit={()=>setEditingProv(p)} onSplit={()=>doSplit(p)}
                  onToggleTopic={(t)=>toggleTopic(p,t)} onToggleKeyword={(k)=>toggleKeyword(p,k)} onReview={(s)=>setReview(p,s)}/>)}
              </div>
            )}
            {artSections.map(sec => {
              const secProvs = struct.provisions.filter(p=>p.section_id===sec.id);
              return (
                <div key={sec.id} className="border-t border-slate-100">
                  <div className="flex items-center justify-between p-3 bg-slate-50/50">
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Section {sec.section_number}</span>
                      <span className="ml-2 font-semibold">{sec.section_title || <span className="text-slate-400 italic">(no title)</span>}</span>
                      <span className="ml-3 text-xs text-slate-500">Pages {sec.starting_page}–{sec.ending_page} · {sec.review_status}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={()=>setEditingSection(sec)}>Edit section</Button>
                  </div>
                  <div className="p-3">
                    {secProvs.map(p => <ProvisionRow key={p.id} prov={p} topics={topics} keywords={keywords}
                      expanded={expanded} setExpanded={setExpanded}
                      onEdit={()=>setEditingProv(p)} onSplit={()=>doSplit(p)}
                      onToggleTopic={(t)=>toggleTopic(p,t)} onToggleKeyword={(k)=>toggleKeyword(p,k)} onReview={(s)=>setReview(p,s)}/>)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Edit modals */}
      {editingArticle && <EditModal title="Edit Article" data={editingArticle} setData={setEditingArticle} fields={[
        {k:"article_number",label:"Number"},{k:"article_title",label:"Title"},{k:"starting_page",label:"Start page",type:"number"},{k:"ending_page",label:"End page",type:"number"},{k:"review_status",label:"Review status",options:["Pending Review","Reviewed","Needs Correction"]},
      ]} onSave={saveArticle}/>}
      {editingSection && <EditModal title="Edit Section" data={editingSection} setData={setEditingSection} fields={[
        {k:"section_number",label:"Number"},{k:"section_title",label:"Title"},{k:"starting_page",label:"Start page",type:"number"},{k:"ending_page",label:"End page",type:"number"},{k:"review_status",label:"Review status",options:["Pending Review","Reviewed","Needs Correction"]},
      ]} onSave={saveSection}/>}
      {editingProv && <EditModal title="Edit Provision" data={editingProv} setData={setEditingProv} fields={[
        {k:"subsection_label",label:"Subsection label"},{k:"page_number",label:"Page",type:"number"},
        {k:"exact_source_text",label:"Exact source text",area:true},
        {k:"normalized_searchable_text",label:"Normalized text",area:true},
        {k:"review_status",label:"Review status",options:["Pending Review","Reviewed","Needs Correction"]},
      ]} onSave={saveProvision}/>}
    </AppShell>
  );
}

function ProvisionRow({prov, topics, keywords, expanded, setExpanded, onEdit, onSplit, onToggleTopic, onToggleKeyword, onReview}) {
  const isOpen = expanded[prov.id];
  const selectedTopics = new Set(prov.topic_ids || []);
  const selectedKws = new Set(prov.keyword_ids || []);
  return (
    <div className={`border rounded-md p-3 mb-2 ${prov.review_status === "Reviewed" ? "border-green-200 bg-green-50/30" : prov.review_status === "Needs Correction" ? "border-red-200 bg-red-50/30" : "border-slate-200"}`} data-testid={`kb-prov-${prov.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-500 flex flex-wrap gap-2">
            <span className="font-mono">{prov.stable_provision_id}</span>
            {prov.subsection_label && <span className="font-semibold">({prov.subsection_label})</span>}
            <span>· p. {prov.page_number}</span>
            <span>· {prov.review_status}</span>
          </div>
          <div className="mt-2 text-sm whitespace-pre-wrap font-serif" data-testid={`kb-prov-text-${prov.id}`}>{prov.exact_source_text}</div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <Button size="sm" variant="outline" onClick={()=>setExpanded({...expanded, [prov.id]: isOpen ? null : "open"})}>{isOpen ? "Collapse" : "Tag/keywords"}</Button>
          <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onSplit}>Split</Button>
          <Button size="sm" variant="ghost" className={expanded[prov.id]==="merge"?"bg-amber-100":""} onClick={()=>setExpanded({...expanded, [prov.id]: expanded[prov.id]==="merge" ? null : "merge"})}>{expanded[prov.id]==="merge"?"✓ Selected for merge":"Select for merge"}</Button>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="text-green-700" onClick={()=>onReview("Reviewed")} data-testid={`kb-review-approve-${prov.id}`}>✓</Button>
            <Button size="sm" variant="ghost" className="text-red-700" onClick={()=>onReview("Needs Correction")}>!</Button>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-semibold text-slate-600 uppercase mb-1">Topics</div>
            <div className="flex flex-wrap gap-1">
              {topics.map(t => (
                <button key={t.id} onClick={()=>onToggleTopic(t.id)}
                  className={`text-xs px-2 py-1 rounded border ${selectedTopics.has(t.id) ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-300 text-slate-700"}`}>
                  {t.name}
                </button>
              ))}
              {topics.length === 0 && <span className="text-xs text-slate-500">No topics defined yet. Add via Topics Manager.</span>}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 uppercase mb-1">Keywords</div>
            <div className="flex flex-wrap gap-1">
              {keywords.map(k => (
                <button key={k.id} onClick={()=>onToggleKeyword(k.id)}
                  className={`text-xs px-2 py-1 rounded border ${selectedKws.has(k.id) ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 text-slate-700"}`}>
                  {k.keyword}
                </button>
              ))}
              {keywords.length === 0 && <span className="text-xs text-slate-500">No keywords defined yet.</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({title, data, setData, fields, onSave}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setData(null)}>
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e=>e.stopPropagation()}>
        <h3 className="text-xl font-bold font-display">{title}</h3>
        <div className="mt-4 space-y-3">
          {fields.map(f=>(
            <label key={f.k} className="block text-sm">
              <span className="font-semibold">{f.label}</span>
              {f.options ? (
                <select className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={data[f.k]||""} onChange={e=>setData({...data,[f.k]:e.target.value})}>
                  {f.options.map(o=><option key={o}>{o}</option>)}
                </select>
              ) : f.area ? (
                <textarea className="mt-1 w-full border border-slate-300 rounded px-2 py-1 font-mono text-xs" rows={8} value={data[f.k]||""} onChange={e=>setData({...data,[f.k]:e.target.value})}/>
              ) : (
                <input type={f.type||"text"} className="mt-1 w-full border border-slate-300 rounded px-2 py-1" value={data[f.k]??""} onChange={e=>setData({...data,[f.k]: f.type==="number" ? parseInt(e.target.value,10)||0 : e.target.value})}/>
              )}
            </label>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={()=>setData(null)}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={onSave}>Save</Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Basic contract viewer
// =============================================================================
export function AdminContractViewer() {
  const [agreements, setAgreements] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [struct, setStruct] = React.useState({articles:[], sections:[], provisions:[]});
  const [filter, setFilter] = React.useState({article:"", section:"", review:""});

  React.useEffect(()=>{ api.get("/admin/contracts").then(r=>{ setAgreements(r.data); if (r.data[0]) setSelected(r.data[0].id); }); }, []);
  React.useEffect(()=>{ if (selected) api.get(`/admin/contracts/${selected}/structure`).then(r=>setStruct(r.data)); }, [selected]);

  const filtered = struct.provisions.filter(p=>{
    if (filter.article) {
      const art = struct.articles.find(a=>a.id===p.article_id);
      if (!art || art.article_number !== filter.article) return false;
    }
    if (filter.section) {
      const sec = struct.sections.find(s=>s.id===p.section_id);
      if (!sec || sec.section_number !== filter.section) return false;
    }
    if (filter.review && p.review_status !== filter.review) return false;
    return true;
  });

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black font-display" data-testid="kb-viewer-title">Contract Viewer</h1>
        <Link to="/admin/contracts"><Button variant="ghost">← Contract Library</Button></Link>
      </div>
      <div className="mt-4 flex flex-wrap gap-3 items-end">
        <label className="text-sm">Agreement
          <select className="mt-1 block border border-slate-300 rounded px-2 py-1" value={selected||""} onChange={e=>setSelected(e.target.value)}>
            {agreements.map(a=><option key={a.id} value={a.id}>{a.title} ({a.version})</option>)}
          </select>
        </label>
        <label className="text-sm">Article
          <input className="mt-1 block border border-slate-300 rounded px-2 py-1 w-24" value={filter.article} onChange={e=>setFilter({...filter, article:e.target.value})}/>
        </label>
        <label className="text-sm">Section
          <input className="mt-1 block border border-slate-300 rounded px-2 py-1 w-24" value={filter.section} onChange={e=>setFilter({...filter, section:e.target.value})}/>
        </label>
        <label className="text-sm">Review status
          <select className="mt-1 block border border-slate-300 rounded px-2 py-1" value={filter.review} onChange={e=>setFilter({...filter, review:e.target.value})}>
            <option value="">Any</option>
            <option>Pending Review</option><option>Reviewed</option><option>Needs Correction</option>
          </select>
        </label>
      </div>
      <p className="mt-3 text-sm text-slate-500">{filtered.length} provisions</p>
      <div className="mt-3 space-y-2" data-testid="kb-viewer-list">
        {filtered.map(p=>{
          const art = struct.articles.find(a=>a.id===p.article_id);
          const sec = struct.sections.find(s=>s.id===p.section_id);
          return (
            <div key={p.id} className="bg-white border border-slate-200 rounded-md p-3">
              <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                <span className="font-mono">{p.stable_provision_id}</span>
                <span>Article {art?.article_number}</span>
                {sec && <span>Section {sec.section_number}</span>}
                {p.subsection_label && <span>({p.subsection_label})</span>}
                <span>p. {p.page_number}</span>
                <span>{p.review_status}</span>
                <span>{p.is_active ? "Active" : "Inactive"}</span>
              </div>
              <div className="mt-2 text-sm whitespace-pre-wrap font-serif">{p.exact_source_text}</div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}

// =============================================================================
// Basic search
// =============================================================================
export function AdminContractSearch() {
  const [agreements, setAgreements] = React.useState([]);
  const [filters, setFilters] = React.useState({q:"",article_number:"",section_number:"",page_number:"",stable_provision_id:"",review_status:"",topic_id:"",keyword_id:"",agreement_id:""});
  const [topics, setTopics] = React.useState([]);
  const [keywords, setKeywords] = React.useState([]);
  const [results, setResults] = React.useState([]);
  const [count, setCount] = React.useState(0);

  React.useEffect(()=>{
    api.get("/admin/contracts").then(r=>setAgreements(r.data));
    api.get("/admin/contracts/meta/topics").then(r=>setTopics(r.data));
    api.get("/admin/contracts/meta/keywords").then(r=>setKeywords(r.data));
  }, []);

  const runSearch = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_,v])=>v));
    const { data } = await api.get("/admin/contracts/search", { params });
    setResults(data.results); setCount(data.count);
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black font-display" data-testid="kb-search-title">Contract Search</h1>
        <Link to="/admin/contracts"><Button variant="ghost">← Contract Library</Button></Link>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input placeholder="Word or phrase" className="border border-slate-300 rounded px-2 py-1" value={filters.q} onChange={e=>setFilters({...filters, q:e.target.value})} data-testid="kb-search-input"/>
        <input placeholder="Article number" className="border border-slate-300 rounded px-2 py-1" value={filters.article_number} onChange={e=>setFilters({...filters, article_number:e.target.value})}/>
        <input placeholder="Section number" className="border border-slate-300 rounded px-2 py-1" value={filters.section_number} onChange={e=>setFilters({...filters, section_number:e.target.value})}/>
        <input placeholder="Page number" type="number" className="border border-slate-300 rounded px-2 py-1" value={filters.page_number} onChange={e=>setFilters({...filters, page_number:e.target.value})}/>
        <input placeholder="Stable provision ID (e.g. NMA-2023-A03)" className="border border-slate-300 rounded px-2 py-1" value={filters.stable_provision_id} onChange={e=>setFilters({...filters, stable_provision_id:e.target.value})}/>
        <select className="border border-slate-300 rounded px-2 py-1" value={filters.agreement_id} onChange={e=>setFilters({...filters, agreement_id:e.target.value})}>
          <option value="">All agreements</option>
          {agreements.map(a=><option key={a.id} value={a.id}>{a.title}</option>)}
        </select>
        <select className="border border-slate-300 rounded px-2 py-1" value={filters.topic_id} onChange={e=>setFilters({...filters, topic_id:e.target.value})}>
          <option value="">Any topic</option>
          {topics.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="border border-slate-300 rounded px-2 py-1" value={filters.keyword_id} onChange={e=>setFilters({...filters, keyword_id:e.target.value})}>
          <option value="">Any keyword</option>
          {keywords.map(k=><option key={k.id} value={k.id}>{k.keyword}</option>)}
        </select>
        <select className="border border-slate-300 rounded px-2 py-1" value={filters.review_status} onChange={e=>setFilters({...filters, review_status:e.target.value})}>
          <option value="">Any review status</option>
          <option>Pending Review</option><option>Reviewed</option><option>Needs Correction</option>
        </select>
      </div>
      <Button className="mt-3 bg-red-600 hover:bg-red-700 text-white" onClick={runSearch} data-testid="kb-search-btn">Search</Button>
      <p className="mt-4 text-sm text-slate-500">{count} results</p>
      <div className="mt-2 space-y-2" data-testid="kb-search-results">
        {results.map(p=>(
          <div key={p.id} className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-xs text-slate-500 font-mono">{p.stable_provision_id} · p. {p.page_number} · {p.review_status}</div>
            <div className="mt-1 text-sm whitespace-pre-wrap font-serif">{p.exact_source_text.slice(0, 400)}{p.exact_source_text.length > 400 ? "…" : ""}</div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

// =============================================================================
// Topics & keywords manager
// =============================================================================
export function AdminContractTaxonomy() {
  const [topics, setTopics] = React.useState([]);
  const [keywords, setKeywords] = React.useState([]);
  const [newTopic, setNewTopic] = React.useState("");
  const [newKw, setNewKw] = React.useState("");

  const load = async () => {
    setTopics((await api.get("/admin/contracts/meta/topics")).data);
    setKeywords((await api.get("/admin/contracts/meta/keywords")).data);
  };
  React.useEffect(() => { load(); }, []);

  const addTopic = async () => { if (!newTopic.trim()) return; try { await api.post("/admin/contracts/meta/topics", { name: newTopic.trim() }); toast.success("Added"); setNewTopic(""); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }};
  const addKw = async () => { if (!newKw.trim()) return; try { await api.post("/admin/contracts/meta/keywords", { keyword: newKw.trim().toLowerCase() }); toast.success("Added"); setNewKw(""); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }};
  const delTopic = async (id) => { if (!window.confirm("Delete this topic?")) return; await api.delete(`/admin/contracts/meta/topics/${id}`); load(); };
  const delKw = async (id) => { if (!window.confirm("Delete this keyword?")) return; await api.delete(`/admin/contracts/meta/keywords/${id}`); load(); };

  const seedDefaults = async () => {
    const defaults = ["Supervisor Working","Seniority","Overtime","Discipline","Warning Letters","Safety","Attendance","Harassment","Subcontracting","Layoffs","Vacations","Technology","Grievance Procedure","Discharge","Job Bidding","Pay Shortage","Leave","Union Rights"];
    for (const name of defaults) { try { await api.post("/admin/contracts/meta/topics", { name }); } catch {/* skip dupes */} }
    toast.success("Seeded default topics"); load();
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black font-display">Topics & Keywords</h1>
        <Link to="/admin/contracts"><Button variant="ghost">← Contract Library</Button></Link>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-bold font-display">Topics ({topics.length})</h2>
            <Button size="sm" variant="outline" onClick={seedDefaults} data-testid="kb-seed-topics">Seed defaults</Button>
          </div>
          <div className="mt-3 flex gap-2">
            <input className="flex-1 border border-slate-300 rounded px-2 py-1" placeholder="New topic name" value={newTopic} onChange={e=>setNewTopic(e.target.value)}/>
            <Button size="sm" onClick={addTopic}>Add</Button>
          </div>
          <ul className="mt-3 space-y-1">
            {topics.map(t=><li key={t.id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1"><span>{t.name}</span><Button size="sm" variant="ghost" className="text-red-600" onClick={()=>delTopic(t.id)}>×</Button></li>)}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-bold font-display">Keywords ({keywords.length})</h2>
          <div className="mt-3 flex gap-2">
            <input className="flex-1 border border-slate-300 rounded px-2 py-1" placeholder="New keyword or phrase" value={newKw} onChange={e=>setNewKw(e.target.value)}/>
            <Button size="sm" onClick={addKw}>Add</Button>
          </div>
          <ul className="mt-3 space-y-1 max-h-96 overflow-y-auto">
            {keywords.map(k=><li key={k.id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1"><span>{k.keyword}</span><Button size="sm" variant="ghost" className="text-red-600" onClick={()=>delKw(k.id)}>×</Button></li>)}
          </ul>
        </div>
      </div>
    </AppShell>
  );
}
